import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import InputCst from "@components/InputCst/InputCst";
import { UsefulLink } from "../../../types";

interface UsefulLinkModalCstProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (link: UsefulLink) => void;
}

const EMPTY: UsefulLink = { url: "", url_label: "", description: "" };

const UsefulLinkModalCst: React.FC<UsefulLinkModalCstProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation("wizard-activity");
  const theme = useTheme();

  const [draft, setDraft] = useState<UsefulLink>(EMPTY);

  useEffect(() => {
    if (open) setDraft(EMPTY);
  }, [open]);

  const canSubmit = draft.url.trim() !== "" && draft.url_label.trim() !== "";

  const handleSubmit = () => {
    if (!canSubmit) return;
    onConfirm({
      url: draft.url.trim(),
      url_label: draft.url_label.trim(),
      description: draft.description?.trim() || undefined,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px 12px",
          color: theme.palette.text.primary,

          borderBottom: 0,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {t("sections.useful-links.modal-title")}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "16px 24px",

          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },

          borderTop: 0,
          borderBottom: 0,

          overflow: "visible",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <InputCst
              id="useful-link-url"
              label={`${t("fields.url")} *`}
              value={draft.url}
              onChange={(e: any) =>
                setDraft((prev) => ({ ...prev, url: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputCst
              id="useful-link-label"
              label={`${t("fields.url-label")} *`}
              value={draft.url_label}
              onChange={(e: any) =>
                setDraft((prev) => ({ ...prev, url_label: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12}>
            <InputCst
              id="useful-link-description"
              label={t("fields.description") || ""}
              value={draft.description ?? ""}
              onChange={(e: any) =>
                setDraft((prev) => ({ ...prev, description: e.target.value }))
              }
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px 20px", gap: 2 }}>
        <Box sx={{ flex: 1 }} />
        <ButtonPrimary size="md" onClick={handleSubmit} disabled={!canSubmit}>
          {t("common.add")}
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  );
};

export default UsefulLinkModalCst;
